import { useEffect, useRef, useState } from "react";
import { CustomSettingData } from "../settings-page";
import { TooltipWrapper } from "../../shared";
import { CoreMenuItem, CoreSelect, CoreTable, CoreTableColumn } from "@verity/components/core";
import NoneFlagSvg from "../../../images/settings/none.svg?react";
import MinorFlagSvg from "../../../images/settings/minor.svg?react";
import MajorFlagSvg from "../../../images/settings/major.svg?react";
import SevereFlagSvg from "../../../images/settings/severe.svg?react";
import { InputBaseComponentsPropsOverrides, Stack } from "@mui/material";
import i18n from "../../../i18n";

export type AnomalyType =
	| "lose_focus"
	| "copy_paste"
	| "web_navigation"
	| "screenshot"
	| "user_media_interrupt"
	| "screen_recording_interrupt"
	| "screen_share_inactive"
	| "monitor_count_interrupt"
	| "speaking_interrupt"
	| "upload_incomplete"
	| "force_stop_proctoring"
	| "no_submission"
	| "connection_lost"
	| "missing_student"
	| "wrong_people"
	| "phone_detected"
	| "computer_detected"
	| "book_detected"
	| "many_objects_detected"
	| "no_proctors"
	| "opens_blocked_app"
	| "enables_blocked_extension"
	| "handoff_interrupt"
	| "blocked_navigation"
	| "block_applications_failed"
	| "side_panel_detected"
	| "right_click_blocked"
	| "printing_blocked"
	| "toggled_block_mobile_apps"
	| "decoy_site_viewed"
	| "face_id_mismatch"
	| "bypassed_ai_detection"
	| "restricted_device_source_connected"
	| "restricted_device_source_disconnected"
	| "restricted_device_source_unlocked"
	| "restricted_device_source_locked"
	| "flagged_keyword"
	| "system_resource_fail"
	| "connection_check_fail"
	| "not_in_geofence"
	| "location_unknown"
	| "ip_address_out_of_range"
	| "ip_address_unknown"
	| "extension_list"
	| "collaboration_detected"
	| "essay_collaboration_detected"
	| "running_applications";

export const ANOMALY_TO_DESCRIPTION: Record<AnomalyType, string> = {
	lose_focus: i18n.t("portal.components.settings.flagLvlConfigTSX.lose_focus"),
	copy_paste: i18n.t("portal.components.settings.flagLvlConfigTSX.copy_paste"),
	web_navigation: i18n.t("portal.components.settings.flagLvlConfigTSX.web_navigation"),
	screenshot: i18n.t("portal.components.settings.flagLvlConfigTSX.screenshot"),
	user_media_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.user_media_interrupt"),
	screen_recording_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.screen_recording_interrupt"),
	screen_share_inactive: i18n.t("portal.components.settings.flagLvlConfigTSX.screen_share_inactive"),
	monitor_count_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.monitor_count_interrupt"),
	missing_student: i18n.t("portal.components.settings.flagLvlConfigTSX.missing_student"),
	wrong_people: i18n.t("portal.components.settings.flagLvlConfigTSX.wrong_people"),
	speaking_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.speaking_interrupt"),
	upload_incomplete: i18n.t("portal.components.settings.flagLvlConfigTSX.upload_incomplete"),
	force_stop_proctoring: i18n.t("portal.components.settings.flagLvlConfigTSX.force_stop_proctoring"),
	no_submission: i18n.t("portal.components.settings.flagLvlConfigTSX.no_submission"),
	connection_lost: i18n.t("portal.components.settings.flagLvlConfigTSX.connection_lost"),
	phone_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.phone_detected"),
	computer_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.computer_detected"),
	book_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.book_detected"),
	many_objects_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.many_objects_detected"),
	no_proctors: i18n.t("portal.components.settings.flagLvlConfigTSX.no_proctors"),
	opens_blocked_app: i18n.t("portal.components.settings.flagLvlConfigTSX.opens_blocked_app"),
	enables_blocked_extension: i18n.t("portal.components.settings.flagLvlConfigTSX.enables_blocked_extension"),
	handoff_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.restrict_handoff_event_desc"),
	blocked_navigation: i18n.t("portal.components.settings.flagLvlConfigTSX.blocked_navigation"),
	block_applications_failed: i18n.t("portal.components.settings.flagLvlConfigTSX.block_applications_failed"),
	side_panel_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.side_panel_detected"),
	right_click_blocked: i18n.t("portal.components.settings.flagLvlConfigTSX.right_click_blocked"),
	printing_blocked: i18n.t("portal.components.settings.flagLvlConfigTSX.printing_blocked"),
	toggled_block_mobile_apps: i18n.t("portal.components.settings.flagLvlConfigTSX.toggled_block_mobile_apps"),
	decoy_site_viewed: i18n.t("portal.components.settings.flagLvlConfigTSX.decoy_site_viewed"),
	face_id_mismatch: i18n.t("portal.components.settings.flagLvlConfigTSX.face_id_mismatch"),
	bypassed_ai_detection: i18n.t("portal.components.settings.flagLvlConfigTSX.bypassed_ai_detection"),
	restricted_device_source_connected: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_connected",
	),
	restricted_device_source_disconnected: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_disconnected",
	),
	restricted_device_source_unlocked: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_unlocked",
	),
	restricted_device_source_locked: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_locked",
	),
	flagged_keyword: i18n.t("portal.components.settings.flagLvlConfigTSX.flagged_keyword"),
	connection_check_fail: i18n.t("portal.components.settings.flagLvlConfigTSX.connection_check_fail"),
	system_resource_fail: i18n.t("portal.components.settings.flagLvlConfigTSX.system_resource_fail"),
	not_in_geofence: i18n.t("portal.components.settings.flagLvlConfigTSX.not_in_geofence"),
	location_unknown: i18n.t("portal.components.settings.flagLvlConfigTSX.location_unknown"),
	ip_address_out_of_range: i18n.t("portal.components.settings.flagLvlConfigTSX.ip_address_out_of_range"),
	ip_address_unknown: i18n.t("portal.components.settings.flagLvlConfigTSX.ip_address_unknown"),
	extension_list: i18n.t("portal.components.settings.flagLvlConfigTSX.extension_list"),
	running_applications: i18n.t("portal.components.settings.flagLvlConfigTSX.running_applications") as string,
	collaboration_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.collaboration_detected2"),
	essay_collaboration_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.essay_collaboration_detected2"),
};

export const ANOMALY_TO_EVENT_NAME: Record<AnomalyType, string> = {
	lose_focus: i18n.t("portal.components.settings.flagLvlConfigTSX.lose_focus2"),
	copy_paste: i18n.t("portal.components.settings.flagLvlConfigTSX.copy_paste2"),
	web_navigation: i18n.t("portal.components.settings.flagLvlConfigTSX.web_navigation2"),
	screenshot: i18n.t("portal.components.settings.flagLvlConfigTSX.screenshot2"),
	user_media_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.user_media_interrupt2"),
	screen_recording_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.screen_recording_interrupt2"),
	screen_share_inactive: i18n.t("portal.components.settings.flagLvlConfigTSX.screen_share_inactive2"),
	monitor_count_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.monitor_count_interrupt2"),
	missing_student: i18n.t("portal.components.settings.flagLvlConfigTSX.missing_student2"),
	wrong_people: i18n.t("portal.components.settings.flagLvlConfigTSX.wrong_people2"),
	speaking_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.speaking_interrupt2"),
	upload_incomplete: i18n.t("portal.components.settings.flagLvlConfigTSX.upload_incomplete2"),
	force_stop_proctoring: i18n.t("portal.components.settings.flagLvlConfigTSX.force_stop_proctoring2"),
	no_submission: i18n.t("portal.components.settings.flagLvlConfigTSX.no_submission2"),
	connection_lost: i18n.t("portal.components.settings.flagLvlConfigTSX.connection_lost2"),
	phone_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.phone_detected2"),
	computer_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.computer_detected2"),
	book_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.book_detected2"),
	many_objects_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.many_objects_detected2"),
	no_proctors: i18n.t("portal.components.settings.flagLvlConfigTSX.no_proctors2"),
	opens_blocked_app: i18n.t("portal.components.settings.flagLvlConfigTSX.opens_blocked_app2"),
	enables_blocked_extension: i18n.t("portal.components.settings.flagLvlConfigTSX.enables_blocked_extension2"),
	handoff_interrupt: i18n.t("portal.components.settings.flagLvlConfigTSX.restrict_handoff_event_name"),
	blocked_navigation: i18n.t("portal.components.settings.flagLvlConfigTSX.blocked_navigation2"),
	block_applications_failed: i18n.t("portal.components.settings.flagLvlConfigTSX.block_applications_failed2"),
	side_panel_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.side_panel_detected_2"),
	right_click_blocked: i18n.t("portal.components.settings.flagLvlConfigTSX.right_click_blocked_2"),
	printing_blocked: i18n.t("portal.components.settings.flagLvlConfigTSX.printing_blocked_2"),
	toggled_block_mobile_apps: i18n.t("portal.components.settings.flagLvlConfigTSX.toggled_block_mobile_apps_2"),
	decoy_site_viewed: i18n.t("portal.components.settings.flagLvlConfigTSX.decoy_site_viewed_2"),
	face_id_mismatch: i18n.t("portal.components.settings.flagLvlConfigTSX.face_id_mismatch_2"),
	bypassed_ai_detection: i18n.t("portal.components.settings.flagLvlConfigTSX.bypassed_ai_detection_2"),
	restricted_device_source_connected: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_connected_2",
	),
	restricted_device_source_disconnected: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_disconnected_2",
	),
	restricted_device_source_unlocked: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_unlocked_2",
	),
	restricted_device_source_locked: i18n.t(
		"portal.components.settings.flagLvlConfigTSX.restricted_device_source_locked_2",
	),
	flagged_keyword: i18n.t("portal.components.settings.flagLvlConfigTSX.flagged_keyword_2"),
	connection_check_fail: i18n.t("portal.components.settings.flagLvlConfigTSX.connection_check_fail2"),
	system_resource_fail: i18n.t("portal.components.settings.flagLvlConfigTSX.system_resource_fail2"),
	not_in_geofence: i18n.t("portal.components.settings.flagLvlConfigTSX.not_in_geofence2"),
	location_unknown: i18n.t("portal.components.settings.flagLvlConfigTSX.location_unknown2"),
	ip_address_out_of_range: i18n.t("portal.components.settings.flagLvlConfigTSX.ip_address_out_of_range_2"),
	ip_address_unknown: i18n.t("portal.components.settings.flagLvlConfigTSX.ip_address_unknown_2"),
	extension_list: i18n.t("portal.components.settings.flagLvlConfigTSX.extension_list2"),
	running_applications: i18n.t("portal.components.settings.flagLvlConfigTSX.running_applications2") as string,
	collaboration_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.collaboration_detected"),
	essay_collaboration_detected: i18n.t("portal.components.settings.flagLvlConfigTSX.essay_collaboration_detected"),
};

export const FlagLevelConfig = ({
	loadData,
	setting,
	onSettingChange,
}: {
	loadData: Record<AnomalyType, number> | null;
	setting: CustomSettingData<Record<AnomalyType, number>>;
	onSettingChange: (value?: Record<AnomalyType, number>) => void;
	disabled?: boolean;
}) => {
	const [data, setData] = useState<
		{ key: AnomalyType; level: number; description: string; name: string; index: number }[] | null
	>(null);

	const onSettingChangeRef = useRef(onSettingChange);
	onSettingChangeRef.current = onSettingChange;

	// Announce the loaded value once on mount so the settings page's dirty-check baseline reflects
	// the actual starting value, rather than staying stuck at `undefined` until the first edit.
	useEffect(() => {
		const initial = setting.value ?? loadData;
		if (initial) {
			onSettingChangeRef.current(initial);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const preProcessData = setting.value ?? loadData;
		if (preProcessData) {
			const tempData = [] as {
				key: AnomalyType;
				level: number;
				description: string;
				name: string;
				index: number;
			}[];
			const keys = Object.keys(preProcessData) as AnomalyType[];
			let index = 0;
			keys.forEach((key) => {
				const level = preProcessData?.[key];
				tempData.push({
					key,
					level: level ?? 0,
					description: ANOMALY_TO_DESCRIPTION[key],
					name: ANOMALY_TO_EVENT_NAME[key],
					index: index,
				});
				index++;
			});
			setData(tempData);
		}
	}, [setting, loadData]);

	const menuItemStyle = {
		display: "flex",
		gap: "9px",
		alignItems: "center",
	};

	const flagOptions = [
		{ value: 0, label: i18n.t("portal.components.settings.flagLvlConfigTSX.none"), icon: <NoneFlagSvg /> },
		{ value: 1, label: i18n.t("portal.components.settings.flagLvlConfigTSX.minor"), icon: <MinorFlagSvg /> },
		{ value: 2, label: i18n.t("portal.components.settings.flagLvlConfigTSX.major"), icon: <MajorFlagSvg /> },
		{ value: 3, label: i18n.t("portal.components.settings.flagLvlConfigTSX.severe"), icon: <SevereFlagSvg /> },
	];

	const cols: CoreTableColumn<{
		level: number;
		description: string;
		name: string;
		index: number;
		key: AnomalyType;
	}>[] = [
		{
			header: i18n.t("portal.components.settings.flagLvlConfigTSX.flagEvent"),
			key: "event",
			cell: (row) => {
				const eventName = row.data.name;
				return TooltipWrapper(
					eventName,
					<div
						style={{
							textOverflow: "ellipsis",
							overflow: "hidden",
							fontSize: "14px",
							fontWeight: 400,
							lineHeight: 1.2,
							color: "#525252",
							maxWidth: "fit-content",
						}}
					>
						{eventName}
					</div>,
				);
			},
			width: "25%",
			truncateString: true,
			align: "left",
			padding: "10px 10px 10px 20px",
			descendingComparator: (a, b) => b.name.localeCompare(a.name),
		},
		{
			header: i18n.t("portal.components.settings.flagLvlConfigTSX.description"),
			key: "description",
			cell: (row) => {
				return row.data.description;
			},
			width: "50%",
			align: "left",
			allowBodyWrap: true,
			descendingComparator: (a, b) => b.description.localeCompare(a.description),
		},
		{
			header: i18n.t("portal.components.settings.flagLvlConfigTSX.flagLvl"),
			key: "level",
			cell: (row) => (
				<CoreSelect
					id={row.data.key}
					className="flag-level-select"
					value={row.data.level}
					aria-label="Flag levels"
					onChange={(e) => {
						if (data) {
							const tmpData = data.slice();
							tmpData[row.data.index].level = Number(e.target.value);
							setData(tmpData);
							onSettingChange({
								...loadData,
								...setting.value,
								[row.data.key]: Number(e.target.value),
							} as Record<AnomalyType, number>);
						}
					}}
					label="Flag levels"
					displayEmpty
					MenuProps={{ sx: menuItemStyle }}
					slotProps={{
						input: {
							sx: {
								...menuItemStyle,
								padding: "5px 9px",
							},
						} as InputBaseComponentsPropsOverrides,
					}}
					sx={{ width: "100%" }}
					renderValue={(selected) => {
						const item = flagOptions.find((opt) => opt.value === selected);
						return item ? (
							<Stack direction="row" alignItems="center" spacing="9px">
								{item.icon}
								<span>{item.label}</span>
							</Stack>
						) : null;
					}}
				>
					{flagOptions.map((opt) => (
						<CoreMenuItem key={opt.value} value={opt.value} sx={menuItemStyle}>
							{opt.icon}
							{opt.label}
						</CoreMenuItem>
					))}
				</CoreSelect>
			),
			width: "25%",
			tableCellContentWidth: "140px",
			align: "left",
			descendingComparator: (a, b) => b.level - a.level,
		},
	];

	return (
		<CoreTable
			variant="settings"
			data={data ?? []}
			columns={cols}
			rowsPerPage={10}
			paginationSize="small"
			showNoResultsOnEmptyData
			hidePaginationOnSinglePage={false}
			loading={data === null}
		/>
	);
};
